// Widget UI — Shadow DOM, state machine.
import { ProtocolClient } from "../protocol/client.js";
import { spawnMinerWorker } from "../worker/spawn.js";
import { defaultApiBase } from "../api_base.js";
import stylesText from "./styles.css?inline";

const PRESETS = { light: 5000, strict: 10000, paranoid: 30000 };

// Min spacing between finalize re-tries so we don't spam the server when records
// arrive in a tight burst. Each new record is a fresh ~95% attempt anyway.
const MIN_RETRY_INTERVAL_MS = 250;

const LABELS = {
  cs: {
    mining: "Nejsem bot",
    checking: "Ověřuji…",
    passed: "Ověřeno",
    failed: "Selhalo",
  },
  en: {
    mining: "I'm not a bot",
    checking: "Verifying…",
    passed: "Verified",
    failed: "Failed",
  },
};

export class CaptchaWidget {
  constructor(container, opts) {
    this._container = container;
    this._opts = opts;
    this._lang = opts.lang || container.dataset?.lang || "cs";
    this._labels = LABELS[this._lang] || LABELS.cs;
    this._state = "loading";
    this._worker = null;
    this._client = null;
    this._templateId = null;
    this._tMinMs = 0;            // server-authoritative min work time
    this._miningSince = 0;       // ms epoch; resets on every new template
    this._pendingFinalize = false;
    this._finalizeTimer = null;
    this._awaitingRetry = false;  // server asked us to keep mining + re-finalize
    this._lastFinalizeAt = 0;     // ms epoch of last finalize sent (rate guard)
    this._retryTimer = null;      // fallback poll if record-hashes dry up
    this._buildDOM();
    this._connect();
    this._startWorkerOnSession = null; // queued init params for worker
  }

  _buildDOM() {
    const host = document.createElement("div");
    host.dataset.theme = this._opts.theme || "auto";
    this._container.appendChild(host);
    const shadow = host.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = stylesText;
    shadow.appendChild(style);
    const widget = document.createElement("div");
    widget.className = "widget";
    widget.innerHTML = `
      <span class="box" aria-hidden="true"></span>
      <span class="label">${this._labels.mining}</span>
      <span class="spinner" aria-hidden="true"></span>
      <span class="brand">Compute</span>
    `;
    widget.setAttribute("role", "checkbox");
    widget.setAttribute("aria-checked", "false");
    widget.setAttribute("tabindex", "0");
    widget.addEventListener("click", () => this._onClick());
    widget.addEventListener("keydown", (e) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        this._onClick();
      }
    });
    shadow.appendChild(widget);
    this._widgetEl = widget;
    this._labelEl = widget.querySelector(".label");
  }

  _connect() {
    this._client = new ProtocolClient({
      apiBase: this._opts.api || defaultApiBase(),
      siteKey: this._opts.sitekey,
      onSession: (m) => this._onSession(m),
      onAck: (m) => this._onAck(m),
      onReject: (m) => this._onReject(m),
      onNewTemplate: (m) => this._onNewTemplate(m),
      onRetry: (m) => this._onRetry(m),
      onPass: (m) => this._onPass(m),
      onFail: (m) => this._onFail(m),
      onError: (e) => console.warn("[captcha] ws error", e),
    });
    this._client.connect();
  }

  _spawnWorker() {
    if (typeof Worker !== "function") {
      console.warn("[captcha] Web Worker not supported, mining inline");
      return null;
    }
    const w = spawnMinerWorker();
    w.onmessage = (e) => {
      const m = e.data;
      if (m.type === "improvement") {
        this._client.sendImprovement(m.nonce, m.hash, m.template_id);
      }
    };
    return w;
  }

  _onSession(m) {
    this._templateId = m.template_id || "init";
    this._tMinMs = Number(m.t_min_ms) || 0;
    this._miningSince = Date.now();
    this._state = "mining";
    if (this._worker === null) this._worker = this._spawnWorker();
    if (this._worker) {
      this._worker.postMessage({
        type: "init",
        skeleton: m.header_skeleton,
        template_id: this._templateId,
        start_nonce: 0,
      });
    }
  }

  _onAck() {
    // A new record-hash was accepted. If the server previously asked us to retry
    // (insufficient_work / too_early), this fresh record is a new ~95% attempt —
    // re-finalize, throttled by MIN_RETRY_INTERVAL_MS.
    if (this._state !== "checking" || !this._awaitingRetry) return;
    if (Date.now() - this._lastFinalizeAt < MIN_RETRY_INTERVAL_MS) return;
    this._sendFinalizeNow();
  }
  _onReject() { /* server didn't accept this one — keep mining */ }

  _onRetry(m) {
    // Soft retry: stay in "checking", worker keeps mining. Primary retry trigger
    // is the next record (_onAck); this timer is the fallback if records dry up.
    this._awaitingRetry = true;
    if (this._retryTimer) clearTimeout(this._retryTimer);
    const delay = Number(m?.retry_after_ms) || 1000;
    this._retryTimer = setTimeout(() => {
      this._retryTimer = null;
      if (this._state === "checking" && this._awaitingRetry) {
        this._sendFinalizeNow();
      }
    }, delay);
  }

  _onNewTemplate(m) {
    this._templateId = m.template_id || "new";
    // Server resets template_changed_at on a new template → its T_min clock
    // restarts too. Mirror it so a queued finalize doesn't fire too_early.
    this._miningSince = Date.now();
    if (this._worker) {
      this._worker.postMessage({
        type: "reset",
        skeleton: m.header_skeleton,
        template_id: this._templateId,
        start_nonce: 0,
      });
    }
    if (this._pendingFinalize) this._scheduleFinalize();
  }

  _onClick() {
    if (this._state !== "mining") return;
    // Record the click, then defer the finalize until the server's T_min has
    // surely elapsed. A premature finalize would hard-fail (too_early) AND burn
    // the IP fail counter — instead we show "Ověřuji…" and submit when ready.
    this._client.sendCheckboxClick(Date.now());
    this._pendingFinalize = true;
    this._state = "checking";
    this._widgetEl.classList.add("checking");
    this._labelEl.textContent = this._labels.checking;
    this._scheduleFinalize();
  }

  _scheduleFinalize() {
    if (!this._pendingFinalize) return;
    if (this._finalizeTimer) {
      clearTimeout(this._finalizeTimer);
      this._finalizeTimer = null;
    }
    // Small margin so the server-side `time_on_template >= T_min` check has
    // certainly passed despite clock skew / message latency.
    const BUFFER_MS = 150;
    const elapsed = Date.now() - (this._miningSince || Date.now());
    const remaining = this._tMinMs - elapsed + BUFFER_MS;
    if (remaining <= 0) {
      this._pendingFinalize = false;
      this._sendFinalizeNow();
    } else {
      this._finalizeTimer = setTimeout(() => {
        this._finalizeTimer = null;
        this._scheduleFinalize();
      }, remaining);
    }
  }

  _sendFinalizeNow() {
    this._lastFinalizeAt = Date.now();
    this._client.sendFinalize();
  }

  _onPass(m) {
    this._clearFinalizeTimer();
    this._state = "passed";
    this._widgetEl.classList.remove("checking");
    this._widgetEl.classList.add("passed");
    this._widgetEl.setAttribute("aria-checked", "true");
    this._labelEl.textContent = this._labels.passed;
    this._injectHiddenInput(m.solve_token);
    this._stopWorker();
    this._client?.close();
    this._opts.onPass?.(m.solve_token);
  }

  _onFail(m) {
    this._clearFinalizeTimer();
    this._state = "failed";
    this._widgetEl.classList.remove("checking");
    this._widgetEl.classList.add("failed");
    this._labelEl.textContent = `${this._labels.failed}: ${m.reason}`;
    this._stopWorker();
    this._client?.close();
    this._opts.onFail?.(m.reason);
  }

  _injectHiddenInput(token) {
    let form = this._container.closest("form");
    if (!form) return;
    let input = form.querySelector('input[name="captcha_token"]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "captcha_token";
      form.appendChild(input);
    }
    input.value = token;
  }

  _clearFinalizeTimer() {
    this._pendingFinalize = false;
    this._awaitingRetry = false;
    if (this._finalizeTimer) {
      clearTimeout(this._finalizeTimer);
      this._finalizeTimer = null;
    }
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
  }

  _stopWorker() {
    if (this._worker) {
      this._worker.postMessage({ type: "stop" });
      this._worker.terminate();
      this._worker = null;
    }
  }

  destroy() {
    this._clearFinalizeTimer();
    this._stopWorker();
    this._client?.close();
    this._container.innerHTML = "";
  }
}

export function presetToTMin(preset) {
  return PRESETS[preset] || PRESETS.strict;
}
